// v0.90.21 — the claim key of a bearer Drop (MDropBearer).
//
// The sender's app makes a one-time secp256k1 key per Drop. Its ADDRESS goes
// on chain (and to the Worker); its PRIVATE half goes in the link after `#`
// and into the sender's own held store, nowhere else. To claim, the holder of
// the link signs EIP-712 Claim(id, recipient) with it — the signature names
// the recipient, so a claim seen in flight can't be redirected.

import { bytesToHex, concat, keccak256, toBytes, type Hex } from "viem";
import { privateKeyToAccount } from "viem/accounts";
import { arcTestnet } from "./chains";

// v1.0.17 — a shorter link. The link carries a 16-byte random SEED (22
// base64url chars, 128 bits) instead of the 32-byte key (43 chars); both
// sides derive the key from it: keccak256("mdrop.claim.v2" ‖ seed). The
// contract, the Worker and the claim signature are unchanged — they only
// ever see the key's address. 128 bits is the standard strength for a bearer
// secret: an attacker who reads the claimKey address off the chain still
// faces ~2^128 guesses. Links made before (43 chars, the key itself) keep
// working: the length says which it is.

export interface ClaimKey {
  /** What travels after `#`: the 16-byte seed, base64url (22 chars). */
  secret: string;
  /** Address of the key — what the contract and the Worker keep. */
  address: string;
}

const b64u = (b: Uint8Array) =>
  btoa(String.fromCharCode(...b)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
const unb64u = (s: string) => {
  const t = s.replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(t + "=".repeat((4 - (t.length % 4)) % 4));
  return Uint8Array.from(bin, (c) => c.charCodeAt(0));
};

const SEED_TAG = toBytes("mdrop.claim.v2");
// secp256k1 group order: a private key is a number in [1, n-1].
const N = 0xfffffffffffffffffffffffffffffffebaaedce6af48a03bbfd25e8cd0364141n;
const validKey = (k: Hex) => { const v = BigInt(k); return v > 0n && v < N; };

/** The claim key a seed stands for (v1.0.17). */
function keyFromSeed(seed: Uint8Array): Hex {
  return keccak256(concat([SEED_TAG, seed]));
}

export function newClaimKey(): ClaimKey {
  for (;;) {
    const seed = crypto.getRandomValues(new Uint8Array(16));
    const pk = keyFromSeed(seed);
    if (!validKey(pk)) continue; // chance ~2^-128; never in practice
    return { secret: b64u(seed), address: privateKeyToAccount(pk).address };
  }
}

/** The private key from a link fragment, or null when there isn't a valid one. */
export function claimKeyFromFragment(hash: string): Hex | null {
  const s = hash.replace(/^#/, "").trim();
  try {
    if (/^[A-Za-z0-9_-]{22}$/.test(s)) { // v1.0.17 — a seed
      const b = unb64u(s);
      if (b.length !== 16) return null;
      const pk = keyFromSeed(b);
      return validKey(pk) ? pk : null;
    }
    if (/^[A-Za-z0-9_-]{43}$/.test(s)) { // before 1.0.17 — the key itself
      const b = unb64u(s);
      return b.length === 32 ? bytesToHex(b) : null;
    }
  } catch {
    /* not base64url */
  }
  return null;
}

/** Sign Claim(id, recipient) for the MDropBearer at `contract`. */
export function signClaim(pk: Hex, contract: string, idBytes32: Hex, recipient: string): Promise<Hex> {
  return privateKeyToAccount(pk).signTypedData({
    domain: {
      name: "MDropBearer",
      version: "1",
      chainId: arcTestnet.id,
      verifyingContract: contract as `0x${string}`,
    },
    types: { Claim: [{ name: "id", type: "bytes32" }, { name: "recipient", type: "address" }] },
    primaryType: "Claim",
    message: { id: idBytes32, recipient: recipient as `0x${string}` },
  });
}

export const BEARER_CREATE_ABI = [
  {
    type: "function",
    name: "create",
    stateMutability: "nonpayable",
    inputs: [
      { name: "id", type: "bytes32" },
      { name: "amount", type: "uint128" },
      { name: "claimKey", type: "address" },
    ],
    outputs: [],
  },
] as const;

export const BEARER_CLAIM_ABI = [
  {
    type: "function",
    name: "claim",
    stateMutability: "nonpayable",
    inputs: [
      { name: "id", type: "bytes32" },
      { name: "recipient", type: "address" },
      { name: "sig", type: "bytes" },
    ],
    outputs: [],
  },
] as const;
